'use client';

import { useState } from 'react';
import { trackEvent } from '@/lib/analytics';

export default function ContactForm({
  nameLabel = 'Name',
  namePlaceholder = 'Your name',
  emailLabel = 'Email',
  emailPlaceholder = 'your@email.com',
  messageLabel = 'Message',
  messagePlaceholder = 'How can we help?',
  submitLabel = 'Send Message',
  submittingLabel = 'Sending…',
  successLabel = 'Thank you. Your message has been sent to Kettura.',
  errorLabel = 'Could not send your message. Please try again or contact us by email.',
  cabin = '',
  initialMessage = '',
}: {
  nameLabel?: string;
  namePlaceholder?: string;
  emailLabel?: string;
  emailPlaceholder?: string;
  messageLabel?: string;
  messagePlaceholder?: string;
  submitLabel?: string;
  submittingLabel?: string;
  successLabel?: string;
  errorLabel?: string;
  cabin?: string;
  initialMessage?: string;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState(initialMessage);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<'success' | 'error' | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    setResult(null);
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, message, cabin }),
      });
      if (!response.ok) throw new Error('Contact submission failed');
      setResult('success');
      setMessage('');
      trackEvent('contact_form_submit', { property: cabin });
    } catch {
      setResult('error');
    } finally {
      setSending(false);
    }
  };

  const inputClasses =
    'w-full rounded-lg px-4 py-3 text-sm outline-none transition-colors duration-300 bg-white border border-[#5A5A6E]/20 text-[#1A1A2E] placeholder-[#5A5A6E] focus:border-[#9B7B3A]';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label
          htmlFor="name"
          className="block text-sm font-medium mb-2 text-[#1A1A2E]"
        >
          {nameLabel}
        </label>
        <input
          id="name"
          required
          maxLength={120}
          type="text"
          placeholder={namePlaceholder}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={inputClasses}
        />
      </div>

      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium mb-2 text-[#1A1A2E]"
        >
          {emailLabel}
        </label>
        <input
          id="email"
          required
          maxLength={254}
          type="email"
          placeholder={emailPlaceholder}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputClasses}
        />
      </div>

      <div>
        <label
          htmlFor="message"
          className="block text-sm font-medium mb-2 text-[#1A1A2E]"
        >
          {messageLabel}
        </label>
        <textarea
          id="message"
          required
          maxLength={5000}
          rows={5}
          placeholder={messagePlaceholder}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className={inputClasses}
        />
      </div>

      <button type="submit" disabled={sending} className="w-full rounded-full bg-[#9B7B3A] px-4 sm:px-6 py-3 text-sm font-semibold text-white hover:bg-[#C4A95E] disabled:opacity-50 md:w-auto">
        {sending ? submittingLabel : submitLabel}
      </button>
      {result === 'success' && <p role="status" className="text-sm text-green-700">{successLabel}</p>}
      {result === 'error' && <p role="alert" className="text-sm text-red-600">{errorLabel}</p>}
    </form>
  );
}
